import { ComponentProps, ComponentType, ReactNode, createElement, useId } from 'react'
import { Box, Button, Checkbox, Tooltip, TooltipProps, VisuallyHidden } from '~/design_system'

type TooltipOptions = Omit<TooltipProps, 'children'> & {
  label: ReactNode
}

type TooltipContent = ReactNode | TooltipOptions

type WithDisabledTooltipExtraProps = {
  disabledTooltip: TooltipContent
  tooltipWhenDisabledOnly?: boolean
}

const DEFAULT_TOOLTIP_PROPS: Pick<TooltipProps, 'openDelay' | 'hasArrow'> = {
  openDelay: 500,
  hasArrow: true
}

type InteractiveDisableableChakraProps = {
  isDisabled?: boolean
}

function normalizeTooltipProps(tooltip: TooltipContent): TooltipOptions {
  if (isTooltipOptions(tooltip)) {
    return tooltip
  }

  return { label: tooltip }
}

function isTooltipOptions(tooltip: TooltipContent): tooltip is TooltipOptions {
  return typeof tooltip === 'object' && tooltip !== null && 'label' in tooltip
}

/**
 * HOC factory that adds a disabled-state tooltip to any component supporting `isDisabled`.
 * Helps get around a Chakra bug as well as native HTML limitations:
 * @see https://github.com/chakra-ui/chakra-ui/issues/500
 *
 * The returned component accepts all original props plus:
 * - `disabledTooltip`: tooltip label/content or full tooltip options (`{ label, ... }`)
 * - `tooltipWhenDisabledOnly` (default `true`): when `true`, tooltip is shown only while disabled
 *
 * @typeParam C - Component type that supports an `isDisabled?: boolean` prop
 * @param Component - Base component to wrap
 * @param options
 * @param options.defaultTooltipProps - Tooltip defaults applied before per-instance tooltip props
 * @param options.transformProps - Optional prop transformer to adapt disabled behavior per component
 * (e.g. convert `isDisabled` to `aria-disabled`, add `isFocusable`, etc.)
 *
 * @example
 * See exported components at bottom of file, `ButtonWithDisabledTooltip` and `CheckboxWithDisabledTooltip`
 * for usage examples.
 */
export function withDisabledTooltip<C extends ComponentType<InteractiveDisableableChakraProps>>(
  Component: C,
  {
    defaultTooltipProps,
    transformProps
  }: {
    defaultTooltipProps?: Partial<Omit<TooltipProps, 'children'>>
    transformProps?: (props: ComponentProps<C>) => ComponentProps<C>
  } = {}
) {
  type Props = ComponentProps<C> & WithDisabledTooltipExtraProps

  /**
   * Bridges Chakra Tooltip trigger props to disabled controls.
   *
   * Why this exists:
   * - Chakra attaches trigger handlers/ref to Tooltip's direct child.
   * - Disabled controls don't reliably emit pointer events, which can cause stuck/open tooltip behavior.
   *
   * This adapter forwards the tooltip ref to a wrapper `<span>` and routes pointer handlers to the wrapper
   * only when the control is disabled, while keeping focus/blur + aria-describedby on the control itself.
   *
   * @see https://github.com/chakra-ui/chakra-ui/issues/500
   */
  function TooltipTriggerAdapter({
    ref,
    ...tooltipTriggerAndComponentProps
  }: ComponentProps<C> & { ref?: React.Ref<HTMLElement> }) {
    const {
      onPointerEnter,
      onPointerDown,
      onFocus,
      onBlur,
      onClick,
      onMouseEnter,
      'aria-describedby': _chakraDescribedBy,
      srDescriptionId,
      ...componentProps
    } = tooltipTriggerAndComponentProps as ComponentProps<C> & {
      srDescriptionId?: string
      onPointerEnter?: (...args: any[]) => void
      onPointerDown?: (...args: any[]) => void
      onFocus?: (...args: any[]) => void
      onBlur?: (...args: any[]) => void
      onClick?: (...args: any[]) => void
      onMouseEnter?: (...args: any[]) => void
      'aria-describedby'?: string
    }

    const isDisabled = componentProps.isDisabled

    return (
      <Box
        as="span"
        ref={ref as never}
        display="inline-flex"
        {...(isDisabled && {
          onPointerEnter,
          onPointerDown,
          onMouseEnter
        })}>
        {createElement(Component, {
          ...(componentProps as ComponentProps<C>),
          ...{
            onFocus,
            onBlur,
            'aria-describedby': srDescriptionId ?? _chakraDescribedBy
          },
          ...(!isDisabled && {
            onPointerEnter,
            onPointerDown,
            onMouseEnter,
            onClick
          })
        })}
      </Box>
    )
  }

  TooltipTriggerAdapter.displayName = `TooltipTriggerAdapter(${Component.displayName || Component.name || 'Component'})`

  function ComponentWithDisabledTooltip(props: Props) {
    const { disabledTooltip, tooltipWhenDisabledOnly = true, ...componentProps } = props
    const tooltipProps = normalizeTooltipProps(disabledTooltip)
    const srDescriptionId = `disabled-tooltip-desc-${useId()}`
    const shouldShowTooltip = tooltipWhenDisabledOnly ? componentProps.isDisabled : true
    const transformedComponentProps =
      transformProps?.(componentProps as ComponentProps<C>) ?? (componentProps as ComponentProps<C>)
    const TooltipTriggerAdapterComponent = TooltipTriggerAdapter as unknown as ComponentType<any>

    return (
      <>
        <Tooltip isDisabled={!shouldShowTooltip} {...DEFAULT_TOOLTIP_PROPS} {...defaultTooltipProps} {...tooltipProps}>
          <TooltipTriggerAdapterComponent
            {...transformedComponentProps}
            srDescriptionId={shouldShowTooltip ? srDescriptionId : undefined}
          />
        </Tooltip>
        {shouldShowTooltip && <VisuallyHidden id={srDescriptionId}>{tooltipProps.label}</VisuallyHidden>}
      </>
    )
  }

  ComponentWithDisabledTooltip.displayName = `WithDisabledTooltip(${
    Component.displayName || Component.name || 'Component'
  })`

  return ComponentWithDisabledTooltip
}

export const ButtonWithDisabledTooltip = withDisabledTooltip(Button, {
  defaultTooltipProps: { placement: 'top' },
  transformProps: ({ isDisabled, onClick, ...props }) => ({
    ...props,
    // Keep button keyboard-focusable while visually/semantically disabled.
    // Do not pass native `disabled`; instead use aria-disabled + blocked click.
    'aria-disabled': isDisabled,
    onClick: isDisabled ? undefined : onClick,
    sx: isDisabled
      ? {
          ...props.sx,
          _focus: { boxShadow: 'outline' }
        }
      : props.sx
  })
})

export const CheckboxWithDisabledTooltip = withDisabledTooltip(Checkbox, {
  transformProps: props => ({
    ...props,
    // Keep disabled checkbox tabbable so keyboard users can discover the tooltip.
    isFocusable: props.isDisabled
  })
})
